import React from 'react'
import { cn } from '@/lib/utils'

/**
 * PROFESSIONAL SLIDE LAYOUT SYSTEM
 *
 * This file defines professional slide layouts with consistent branding,
 * typography, and spacing while providing visual variety through different
 * layout patterns and color schemes.
 *
 * Key Design Principles:
 * - Consistent branding and colors
 * - Professional typography hierarchy
 * - Safe content areas with proper margins
 * - Multiple layout variants for different content types
 * - Responsive design for different screen sizes
 */

// ============================================
// BASE PROFESSIONAL LAYOUT
// ============================================
export interface ProfessionalSlideLayoutProps {
  children: React.ReactNode
  className?: string
  variant?: 'primary' | 'secondary' | 'accent' | 'light' | 'dark' | 'gradient'
  pattern?: 'none' | 'subtle-grid' | 'subtle-dots' | 'subtle-waves'
}

export const ProfessionalSlideLayout: React.FC<ProfessionalSlideLayoutProps> = ({
  children,
  className,
  variant = 'light',
  pattern = 'none',
}) => {
  const variantClasses = {
    primary: 'bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white',
    secondary: 'bg-gradient-to-br from-secondary-600 via-secondary-700 to-secondary-800 text-white',
    accent: 'bg-gradient-to-br from-accent-600 via-accent-700 to-accent-800 text-white',
    light: 'bg-white dark:bg-gray-950 text-gray-900 dark:text-white',
    dark: 'bg-gray-900 dark:bg-gray-950 text-white',
    gradient: 'bg-gradient-to-br from-primary-600 via-secondary-600 to-accent-600 text-white',
  }

  const patternClasses = {
    'none': '',
    'subtle-grid': 'bg-grid-white/[0.02] bg-grid-black/[0.02] dark:bg-grid-white/[0.02]',
    'subtle-dots': 'bg-dots-white/[0.02] bg-dots-black/[0.02] dark:bg-dots-white/[0.02]',
    'subtle-waves': 'bg-waves-white/[0.02] bg-waves-black/[0.02] dark:bg-waves-white/[0.02]',
  }

  return (
    <div className={cn(
      'w-full h-full',
      'p-16', // Generous padding for professional look
      'flex flex-col',
      'overflow-hidden',
      'relative', // For pattern overlays
      variantClasses[variant],
      patternClasses[pattern],
      className
    )}>
      {children}
    </div>
  )
}

// ============================================
// TITLE SLIDE LAYOUT
// ============================================
export interface TitleSlideLayoutProps {
  title: string
  subtitle?: string
  author?: string
  date?: string
  company?: string
  tags?: string[]
  variant?: 'primary' | 'secondary' | 'accent' | 'gradient'
  logo?: React.ReactNode
}

export const TitleSlideLayout: React.FC<TitleSlideLayoutProps> = ({
  title,
  subtitle,
  author,
  date,
  company,
  tags,
  variant = 'primary',
  logo,
}) => {
  return (
    <ProfessionalSlideLayout variant={variant} pattern="subtle-grid">
      <div className="flex-1 flex flex-col justify-center items-center text-center">
        {/* Tags */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-8 justify-center animate-fade-in">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/30"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Main Title */}
        <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-8 animate-fade-in delay-1 leading-tight max-w-6xl">
          {title}
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-2xl md:text-3xl lg:text-4xl mb-16 animate-fade-in delay-2 opacity-90 max-w-5xl leading-relaxed">
            {subtitle}
          </p>
        )}

        {/* Author & Company Info */}
        <div className="animate-fade-in delay-3 opacity-90 space-y-2">
          {author && (
            <p className="text-xl font-semibold">{author}</p>
          )}
          {company && (
            <p className="text-lg opacity-80">{company}</p>
          )}
          {date && (
            <p className="text-base opacity-70">{date}</p>
          )}
        </div>

        {/* Logo */}
        {logo && (
          <div className="mt-16 animate-fade-in delay-4 opacity-80">
            {logo}
          </div>
        )}
      </div>
    </ProfessionalSlideLayout>
  )
}

// ============================================
// SECTION DIVIDER SLIDE LAYOUT
// ============================================
export interface SectionDividerLayoutProps {
  title: string
  subtitle?: string
  sectionNumber?: number
  variant?: 'primary' | 'secondary' | 'accent' | 'gradient'
  icon?: React.ReactNode
}

export const SectionDividerLayout: React.FC<SectionDividerLayoutProps> = ({
  title,
  subtitle,
  sectionNumber,
  variant = 'secondary',
  icon,
}) => {
  return (
    <ProfessionalSlideLayout variant={variant} pattern="subtle-waves">
      <div className="flex-1 flex flex-col justify-center items-center text-center">
        {/* Section Number */}
        {sectionNumber && (
          <div className="mb-8 animate-fade-in">
            <span className="text-8xl font-bold opacity-30">{sectionNumber.toString().padStart(2, '0')}</span>
          </div>
        )}

        {/* Icon */}
        {icon && (
          <div className="mb-8 animate-fade-in delay-1 text-6xl opacity-80">
            {icon}
          </div>
        )}

        {/* Title */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in delay-2 leading-tight max-w-5xl">
          {title}
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-xl md:text-2xl animate-fade-in delay-3 opacity-80 max-w-4xl leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
    </ProfessionalSlideLayout>
  )
}

// ============================================
// CONTENT SLIDE LAYOUT
// ============================================
export interface ContentSlideLayoutProps {
  title: string
  subtitle?: string
  children: React.ReactNode
  variant?: 'light' | 'dark'
  layout?: 'standard' | 'wide' | 'centered'
  showDivider?: boolean
}

export const ContentSlideLayout: React.FC<ContentSlideLayoutProps> = ({
  title,
  subtitle,
  children,
  variant = 'light',
  layout = 'standard',
  showDivider = true,
}) => {
  return (
    <ProfessionalSlideLayout variant={variant}>
      <div className="flex-1 flex flex-col justify-center">
        {/* Header */}
        <header className="mb-12 flex-shrink-0 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight text-primary-600 dark:text-primary-400">
            {title}
          </h1>
          {subtitle && (
            <h2 className="text-xl md:text-2xl opacity-80 leading-relaxed">
              {subtitle}
            </h2>
          )}
          {showDivider && (
            <div className="mt-6 w-20 h-1 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full mx-auto"></div>
          )}
        </header>

        {/* Content */}
        <main className={cn(
          'flex-1 flex flex-col justify-center',
          'overflow-hidden',
          layout === 'centered' && 'items-center text-center',
          layout === 'wide' && 'max-w-none',
          layout === 'standard' && 'max-w-6xl mx-auto w-full'
        )}>
          {children}
        </main>
      </div>
    </ProfessionalSlideLayout>
  )
}

// ============================================
// TWO-COLUMN SLIDE LAYOUT
// ============================================
export interface TwoColumnSlideLayoutProps {
  title: string
  subtitle?: string
  leftContent: React.ReactNode
  rightContent: React.ReactNode
  variant?: 'light' | 'dark'
  columnRatio?: '1:1' | '1:2' | '2:1' | '2:3' | '3:2'
  gap?: 'sm' | 'md' | 'lg'
}

export const TwoColumnSlideLayout: React.FC<TwoColumnSlideLayoutProps> = ({
  title,
  subtitle,
  leftContent,
  rightContent,
  variant = 'light',
  columnRatio = '1:1',
  gap = 'lg',
}) => {
  const ratioClasses = {
    '1:1': 'grid-cols-2',
    '1:2': 'grid-cols-3',
    '2:1': 'grid-cols-3',
    '2:3': 'grid-cols-5',
    '3:2': 'grid-cols-5',
  }

  const leftSpanClasses = {
    '1:1': 'col-span-1',
    '1:2': 'col-span-1',
    '2:1': 'col-span-2',
    '2:3': 'col-span-2',
    '3:2': 'col-span-3',
  }

  const rightSpanClasses = {
    '1:1': 'col-span-1',
    '1:2': 'col-span-2',
    '2:1': 'col-span-1',
    '2:3': 'col-span-3',
    '3:2': 'col-span-2',
  }

  const gapClasses = {
    sm: 'gap-8',
    md: 'gap-12',
    lg: 'gap-16',
  }

  return (
    <ProfessionalSlideLayout variant={variant}>
      <div className="flex-1 flex flex-col justify-center">
        {/* Header */}
        <header className="mb-12 flex-shrink-0 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight text-primary-600 dark:text-primary-400">
            {title}
          </h1>
          {subtitle && (
            <h2 className="text-xl md:text-2xl opacity-80 leading-relaxed">
              {subtitle}
            </h2>
          )}
          <div className="mt-6 w-20 h-1 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full mx-auto"></div>
        </header>

        {/* Two Column Content */}
        <main className={cn(
          'flex-1 grid',
          ratioClasses[columnRatio],
          gapClasses[gap],
          'overflow-hidden'
        )}>
          <div className={cn(leftSpanClasses[columnRatio], 'flex flex-col')}>
            {leftContent}
          </div>
          <div className={cn(rightSpanClasses[columnRatio], 'flex flex-col')}>
            {rightContent}
          </div>
        </main>
      </div>
    </ProfessionalSlideLayout>
  )
}

// ============================================
// SHOWCASE SLIDE LAYOUT (for demos, images)
// ============================================
export interface ShowcaseSlideLayoutProps {
  title?: string
  subtitle?: string
  children: React.ReactNode
  variant?: 'dark' | 'primary'
  fullScreen?: boolean
}

export const ShowcaseSlideLayout: React.FC<ShowcaseSlideLayoutProps> = ({
  title,
  subtitle,
  children,
  variant = 'dark',
  fullScreen = false,
}) => {
  if (fullScreen) {
    return (
      <div className={cn(
        'w-full h-full flex flex-col justify-center items-center',
        variant === 'dark' ? 'bg-gray-900 text-white' : 'bg-gradient-to-br from-primary-600 to-primary-800 text-white'
      )}>
        {children}
      </div>
    )
  }

  return (
    <ProfessionalSlideLayout variant={variant}>
      {/* Optional Header */}
      {(title || subtitle) && (
        <header className="mb-12 flex-shrink-0 text-center">
          {title && (
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              {title}
            </h1>
          )}
          {subtitle && (
            <h2 className="text-xl md:text-2xl opacity-80 leading-relaxed">
              {subtitle}
            </h2>
          )}
        </header>
      )}

      {/* Showcase Content */}
      <main className="flex-1 flex flex-col justify-center items-center">
        {children}
      </main>
    </ProfessionalSlideLayout>
  )
}

// ============================================
// TEAM/CONTACT SLIDE LAYOUT
// ============================================
export interface TeamSlideLayoutProps {
  title: string
  subtitle?: string
  members: Array<{
    name: string
    role: string
    email?: string
    image?: string
    social?: Array<{ platform: string; url: string; icon?: React.ReactNode }>
  }>
  variant?: 'light' | 'primary'
}

export const TeamSlideLayout: React.FC<TeamSlideLayoutProps> = ({
  title,
  subtitle,
  members,
  variant = 'light',
}) => {
  return (
    <ProfessionalSlideLayout variant={variant}>
      {/* Header */}
      <header className="mb-12 flex-shrink-0 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight text-primary-600 dark:text-primary-400">
          {title}
        </h1>
        {subtitle && (
          <h2 className="text-xl md:text-2xl opacity-80 leading-relaxed max-w-4xl mx-auto">
            {subtitle}
          </h2>
        )}
        <div className="mt-6 w-20 h-1 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full mx-auto"></div>
      </header>

      {/* Team Members */}
      <main className="flex-1 flex justify-center items-center">
        <div className={cn(
          'grid gap-12 w-full max-w-6xl',
          members.length === 1 && 'grid-cols-1 justify-items-center',
          members.length === 2 && 'grid-cols-2',
          members.length === 3 && 'grid-cols-3',
          members.length >= 4 && 'grid-cols-2 lg:grid-cols-4'
        )}>
          {members.map((member, index) => (
            <div
              key={index}
              className={cn(
                'text-center space-y-4 animate-fade-in',
                `delay-${index + 1}`
              )}
            >
              {/* Profile Image */}
              {member.image ? (
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center text-white text-4xl font-bold">
                  {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
              )}

              {/* Name & Role */}
              <div>
                <h3 className="text-xl font-bold mb-1">{member.name}</h3>
                <p className="text-base opacity-80">{member.role}</p>
                {member.email && (
                  <p className="text-sm opacity-70 mt-1">{member.email}</p>
                )}
              </div>

              {/* Social Links */}
              {member.social && member.social.length > 0 && (
                <div className="flex justify-center space-x-3">
                  {member.social.map((social, socialIndex) => (
                    <a
                      key={socialIndex}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 transition-colors"
                    >
                      {social.icon || social.platform}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </ProfessionalSlideLayout>
  )
}
